/**
 * web-config — ตรวจ + normalize config ของเว็บ (ADR-010 runtime config)
 *
 * 🔴 **ไฟล์นี้ไม่รู้จัก `window` และไม่รู้ว่าค่ามาจากไหน** — รับ `source` ทาง param เสมอ
 *    **แอปเป็นคนหาค่ามาให้**: Vite อ่านจาก `window.__ELITE_CONFIG__` ที่ `/config.js`
 *    ตั้งไว้ · วันที่ย้ายไป Next/Remix ฝั่ง server อ่านจาก env แล้วส่งค่าเข้ามาที่นี่
 *    ⇒ **กติกาการตรวจไม่ต้องเขียนใหม่ตอนย้าย framework** ซึ่งคือเหตุผลที่มันอยู่ใน lib
 *
 *    หลักเดียวกับที่ `ws-node-app/baseline` บังคับฝั่ง backend อยู่แล้ว
 *    (*"lib รับ config ทาง param ห้ามอ่าน `process.env` เอง"*) — ฝั่งเว็บก็ไม่ต่างกัน
 *    แค่เปลี่ยนชื่อ global
 *
 * 🚨 ค่าทุกตัวที่นี่ **ผู้ใช้เปิดดูได้จาก DevTools** (เหมือนตอนอยู่ใน bundle ไม่ได้แย่ลง)
 *    ⇒ ห้ามใส่ secret เด็ดขาด · LIFF ID / Google client id เป็น public ตามสเปกอยู่แล้ว
 *    **Channel secret ไม่ใช่** — อยู่ฝั่ง `member--webapi` เท่านั้น
 */
export type WebConfig = {
  apiBaseUrl: string;
  liffId: string;
  /**
   * `undefined` = **ปิดช่องทาง login ด้วย Google** (หน้า `/login` จะบอกว่ายังไม่เปิด)
   *
   * 🔴 optional โดยตั้งใจ หลักเดียวกับฝั่ง backend
   *    ⚠️ **"ตั้งใจปิด" กับ "ลืมตั้ง env" แยกกันที่ `render-config.mjs`** ไม่ใช่ที่นี่ —
   *      ตัวนั้นบังคับว่าต้องประกาศตัวแปรมา (ค่าว่างได้) ⇒ มาถึงที่นี่แล้วว่าง
   *      แปลว่า **ตั้งใจ** เสมอ
   */
  googleClientId: string | undefined;
};

/** รูปดิบที่แอปส่งเข้ามา — ตรงกับ `config.template.js` ของ Vite app */
export type WebConfigSource = {
  /**
   * ⚠️ `| undefined` ชัด ๆ ไม่ใช่แค่ `?:` — repo เปิด `exactOptionalPropertyTypes`
   *    ค่ามาจาก JS ที่เบราว์เซอร์ eval ⇒ key หายหรือเป็น `undefined` ได้ทั้งคู่จริง
   */
  apiBaseUrl?: string | undefined;
  liffId?: string | undefined;
  googleClientId?: string | undefined;
};

/**
 * ตรวจ config ที่แอปหามาให้
 *
 * @param source `undefined` = **แอปหาค่าไม่เจอเลย** ไม่ใช่ "ค่าขาด" — ทางแก้คนละทาง
 *   ⇒ ข้อความจึงต้องแยกกัน (ดูเทสของไฟล์นี้)
 */
export function loadWebConfig(source: WebConfigSource | undefined): WebConfig {
  /**
   * 🔴 **ไม่มี source เลย = `/config.js` ไม่ถูกโหลด** — ไม่ใช่ "ค่าขาด"
   *    เกิดได้ 2 ทางและทั้งคู่ต้องบอกให้ตรง: ตอน dev = Vite plugin ไม่ทำงาน
   *    หรือบน server = ขั้นตอน deploy ไม่ได้เขียน `config.js` ลง dist
   *    ⇒ แยกข้อความจากเคส "ค่าขาด" เพราะทางแก้คนละทางกัน
   */
  if (source === undefined) {
    throw new Error(
      'ไม่พบ window.__ELITE_CONFIG__ — /config.js ไม่ถูกโหลด\n' +
        '· ในเครื่อง: plugin `elite-runtime-config` เสิร์ฟให้ตอน `pnpm dev`/`preview` — เช็คว่ามันอยู่ใน vite.config.ts\n' +
        '· บน server: ขั้นตอน deploy ไม่ได้เขียน dist/config.js (ดู ADR-010 §C.4)',
    );
  }

  /**
   * `?? ''` ทำให้ 2 ตัวนี้เป็น `string` เสมอ ⇒ narrow ได้หลังเช็ค `missing`
   * (ไม่ต้องใช้ `as` ซึ่งจะกลบเคสที่ค่าหายจริงไปด้วย)
   */
  const apiBaseUrl = source.apiBaseUrl ?? '';
  const liffId = source.liffId ?? '';

  /** ล้มตอน boot ถ้าค่าขาด — ไม่ปล่อยให้ไปพังตอนกด login */
  const missing = [
    apiBaseUrl.length === 0 ? 'apiBaseUrl' : null,
    liffId.length === 0 ? 'liffId' : null,
  ].filter((name): name is string => name !== null);

  if (missing.length > 0) {
    throw new Error(
      `config ที่ /config.js ขาดค่า: ${missing.join(', ')} — ดู config.template.js + env ของ environment นั้น`,
    );
  }

  const googleClientId = source.googleClientId?.trim();

  return {
    apiBaseUrl,
    liffId,
    googleClientId:
      googleClientId === undefined || googleClientId.length === 0 ? undefined : googleClientId,
  };
}
